import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, CardContent, CircularProgress, Grid, Typography, Tooltip as ReactTooltip } from '@material-ui/core';
import { DashboardApi } from 'services';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { NotificationsList } from './components';
import { toastMessages } from 'config/constants';
import moment from 'moment';
import notificationIcon from '_assets/dashboard/notification.svg';
import revenueIcon from '_assets/dashboard/revenue.svg';
import startIcon from '_assets/dashboard/star.svg';
import toast from 'react-hot-toast';
import useStyles from './styles';
import { useTheme } from '@material-ui/core/styles';
import { useCompany } from 'contexts/CompanyContext';
import { getTextWidth } from 'utils';

const Dashboard = () => {
  const classes = useStyles();

  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [clientsCount, setClientsCount] = useState(0);
  const [newClientsCount, setNewClientsCount] = useState(0);
  const [ratingsCount, setRatingsCount] = useState(0);
  const [ratingsAverage, setRatingsAverage] = useState(0);
  const [topService, setTopService] = useState(null);
  const [topServiceSalesCount, setTopServiceSalesCount] = useState(0);
  const [earnings, setEarnings] = useState({});
  const [annualEarning, setAnnualEarning] = useState(0);
  const [monthlyEarning, setMonthlyEarning] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [pending, setPending] = useState(0);
  const [executed, setExecuted] = useState(0);
  const { currentCompany: company } = useCompany();
  const theme = useTheme();

  const getDashboard = useCallback(
    async function () {
      setIsDashboardLoading(true);

      try {
        const dashboard_data = await DashboardApi.getDashboard(company.id);
        setClientsCount(dashboard_data.clients_count);
        setNewClientsCount(dashboard_data.new_clients_count);
        if (dashboard_data.most_sold_service) {
          setTopService(dashboard_data.most_sold_service);
          setTopServiceSalesCount(dashboard_data.most_sold_service_count);
        }
        setTotalRevenue(dashboard_data.totalRevenue);
        setPending(dashboard_data.pending);
        setExecuted(dashboard_data.executed);
        setEarnings(dashboard_data.earnings);
        setAnnualEarning(dashboard_data.annualEarning);
        setMonthlyEarning(dashboard_data.earnings[4].earnings);
        if (dashboard_data.ratings_sum) setRatingsCount(dashboard_data.ratings_sum);
        if (dashboard_data.ratings_average) setRatingsAverage(dashboard_data.ratings_average);
      } catch (error) {
        toast.error(toastMessages.error.FETCH_DASHBOARD);
      }

      setIsDashboardLoading(false);
    },
    [company.id],
  );

  useEffect(() => {
    setTimeout(getDashboard);
  }, [getDashboard]);

  return (
    <Grid container justifyContent="center" alignItems="center" spacing={3}>
      <Grid container item xs={12} justifyContent="center">
        <Typography variant="h1">Tableau de bord</Typography>
      </Grid>

      <Grid container item spacing={3} justifyContent="center">
        <Grid item xs={8}>
          <Box className={classes.cardHeader}>
            <img alt="revenu" src={revenueIcon} />
            <Typography className={classes.cardTitle} variant="h4" gutterBottom>
              Chiffre d'affaire
            </Typography>
          </Box>
          <Card style={{ padding: 0 }}>
            <CardContent
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                backgroundColor: theme.palette.blue.lightest,
              }}
            >
              <Grid container xs={12} style={{ marginLeft: theme.spacing(4), marginRight: theme.spacing(4) }}>
                <Grid item container xs={6} style={{ display: 'flex', justifyContent: 'center' }}>
                  <Typography color="secondary" className={classes.titleCA}>
                    Mensuel:
                  </Typography>
                  <Typography color="primary" className={classes.valueCA}>
                    {monthlyEarning + ' €'}
                  </Typography>
                </Grid>
                <Grid item container xs={6} style={{ display: 'flex', justifyContent: 'center' }}>
                  <Typography color="secondary" className={classes.titleCA}>
                    Annuel:
                  </Typography>
                  <Typography color="primary" className={classes.valueCA}>
                    {annualEarning + ' €'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Card className={classes.cardRoot} style={{ height: '90%' }}>
            <CardContent className={classes.cardContainer}>
              {isDashboardLoading ? (
                <Box className={classes.cardLoading}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box className={classes.cardContent}>
                  <Box style={{ marginLeft: '50px' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography>Solde du compte</Typography>
                        <Typography>{totalRevenue}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography>Exécuté</Typography>
                        <Typography>{executed}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography>En cours</Typography>
                        <Typography>- {pending}</Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box className={classes.graphContainer}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        width={670}
                        height={200}
                        data={earnings}
                        margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                      >
                        <Line type="monotone" dataKey="earnings" stroke="#8884d8" />
                        <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={4}>
          <Box className={classes.cardHeader}>
            <img alt="notifications" src={notificationIcon} width={30} />
            <Typography className={classes.cardTitle} variant="h4" gutterBottom>
              Notifications
            </Typography>
          </Box>

          <NotificationsList classes={classes} />
        </Grid>
      </Grid>

      {!isDashboardLoading && (
        <Grid className={classes.infoCards} container item xs={12}>
          <Grid item xs={3} sm={4} md={4} lg={3} xl={3} className={classes.infoCardContainer}>
            <Typography noWrap variant="h5">
              Clients
            </Typography>
            <Card className={classes.infoCard}>
              <CardContent className={classes.infoCardContent}>
                <Typography className={classes.infoCardTitle} gutterBottom>
                  Janvier à Dec {moment().get('year')}
                </Typography>
                <Typography className={classes.infoCardBigValue} gutterBottom>
                  {clientsCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={3} sm={4} md={4} lg={3} xl={3} className={classes.infoCardContainer}>
            <Typography noWrap variant="h5">
              Nouveau clients
            </Typography>
            <Card className={classes.infoCard}>
              <CardContent className={classes.infoCardContent}>
                <Typography className={classes.infoCardTitle} gutterBottom>
                  Janvier à Dec {moment().get('year')}
                </Typography>
                <Typography className={classes.infoCardBigValue} gutterBottom>
                  {newClientsCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={3} sm={4} md={4} lg={3} xl={3} className={classes.infoCardContainer}>
            <Typography noWrap variant="h5">
              Note
            </Typography>
            <Card className={classes.infoCard}>
              <CardContent className={classes.infoCardContent}>
                <Typography className={classes.infoCardTitle} gutterBottom>
                  {ratingsCount} votes
                </Typography>
                <Box className={classes.starContent}>
                  <Typography className={classes.infoCardBigValue} gutterBottom>
                    {ratingsAverage}
                  </Typography>
                  <img alt="Etoile" src={startIcon} width={20} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item sm={10} md={10} lg={3} xl={4} className={classes.lastInfoCardContainer} style={{ flexGrow: 1 }}>
            <Typography noWrap variant="h5">
              Produit le plus vendu
            </Typography>
            <Card className={classes.infoCard}>
              <CardContent>
                <Grid container>
                  <Grid container item xs={6} className={classes.mostSelledValueContainerLeft}>
                    <Typography noWrap className={classes.infoCardSubTitle}>
                      Nom
                    </Typography>
                    {topService ?
                        getTextWidth(topService.name, theme.typography.h1.font) >= 160 ? (
                      <ReactTooltip title={topService.name}>
                        <Typography noWrap className={classes.infoCardValue}>
                          {topService.name}
                        </Typography>
                      </ReactTooltip>
                    ) : (
                      <Typography noWrap className={classes.infoCardValue}>
                        {topService?.name}
                      </Typography>
                    ) : (
                            <Typography noWrap className={classes.infoCardValue}>
                              -
                            </Typography>
                        )}
                  </Grid>
                  <Grid container item xs={6} className={classes.mostSelledValueContainerRight}>
                    <Typography noWrap className={classes.infoCardSubTitle}>
                      Nombre de vente
                    </Typography>
                    <Typography className={classes.infoCardBigValue}>{topServiceSalesCount}</Typography>
                  </Grid>
                  <Grid container item xs={6} className={classes.mostSelledValueContainerLeft}>
                    <Typography noWrap className={classes.infoCardSubTitle}>
                      Id
                    </Typography>
                    <Typography className={classes.infoCardValue}>{topService?.identifier}</Typography>
                  </Grid>
                  <Grid container item xs={6} className={classes.mostSelledValueContainerRight}>
                    <Typography noWrap className={classes.infoCardSubTitle}>
                      Note du produit
                    </Typography>
                    <Typography className={classes.infoCardBigValue}>-</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default Dashboard;
